import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, MapPin, Calendar, Copy, ExternalLink } from "lucide-react";
import Logo from "../components/Logo";

// ─── Helpers ────────────────────────────────────────────────────────────────

const LEVEL_COLORS = {
  Bronze:   { bg: "#FDF3E7", border: "#CD7F32", text: "#92400E" },
  Silver:   { bg: "#F3F4F6", border: "#C0C0C0", text: "#374151" },
  Gold:     { bg: "#FFFBEB", border: "#FFD700", text: "#92400E" },
  Platinum: { bg: "#F9FAFB", border: "#E5E4E2", text: "#374151" },
};

const CONDUCT_COLOR = (label) => {
  const pos = ["Responsive", "Fair", "Professional"];
  const neg = ["Poor"];
  if (pos.includes(label)) return { bg: "#DCFCE7", text: "#15803D" };
  if (neg.includes(label)) return { bg: "#FEE2E2", text: "#DC2626" };
  return { bg: "#FEF3C7", text: "#B45309" };
};

function ConductPill({ label }) {
  if (!label) return null;
  const { bg, text } = CONDUCT_COLOR(label);
  return (
    <span style={{
      background: bg, color: text,
      fontSize: 12, fontWeight: 600,
      padding: "4px 10px", borderRadius: 999,
      display: "inline-block",
    }}>{label}</span>
  );
}

function StatBox({ value, label }) {
  return (
    <div style={{ background: "#111", borderRadius: 12, padding: "14px 10px", textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#F97316" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ─── Section renders ─────────────────────────────────────────────────────────

function TenantSection({ profile }) {
  const c = profile.conduct_scores || {};
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#9CA3AF", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Rental History</div>
        <div style={{ fontSize: 13, color: "#D1D5DB" }}>
          Renting since {profile.member_since}
        </div>
        {profile.average_rating && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: "#F97316" }}>⭐ {profile.average_rating.avg}</span>
            <span style={{ fontSize: 12, color: "#6B7280" }}>· rated by landlords & brokers ({profile.average_rating.count} reviews)</span>
          </div>
        )}
      </div>
      {Object.keys(c).length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#9CA3AF", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Rent Conduct</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <ConductPill label={c.communication} />
            <ConductPill label={c.fairness} />
            <ConductPill label={c.professionalism} />
            {c.would_recommend_pct !== undefined && (
              <ConductPill label={`${c.would_recommend_pct}% recommend`} />
            )}
          </div>
        </div>
      )}
      <div style={{ fontSize: 13, color: "#6B7280" }}>
        Deals: <strong style={{ color: "#D1D5DB" }}>{profile.completed_rentals_range} completed</strong>
      </div>
    </div>
  );
}

function LandlordSection({ profile }) {
  const c = profile.conduct_scores || {};
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#9CA3AF", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Property History</div>
        <div style={{ fontSize: 13, color: "#D1D5DB" }}>Renting out since {profile.member_since}</div>
        {profile.average_rating && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: "#F97316" }}>⭐ {profile.average_rating.avg}</span>
            <span style={{ fontSize: 12, color: "#6B7280" }}>· rated by tenants & brokers ({profile.average_rating.count} reviews)</span>
          </div>
        )}
      </div>
      {Object.keys(c).length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#9CA3AF", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Landlord Conduct</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <ConductPill label={c.fairness} />
            <ConductPill label={c.communication} />
            <ConductPill label={c.professionalism} />
            {c.would_recommend_pct !== undefined && (
              <ConductPill label={`${c.would_recommend_pct}% recommend`} />
            )}
          </div>
        </div>
      )}
      <div style={{ fontSize: 13, color: "#6B7280" }}>
        Deals: <strong style={{ color: "#D1D5DB" }}>{profile.completed_rentals_range} completed</strong>
      </div>
    </div>
  );
}

function BrokerSection({ profile }) {
  const c = profile.conduct_scores || {};
  const level = profile.broker_level;
  const levelStyle = level ? LEVEL_COLORS[level] : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#9CA3AF", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Agent Profile</div>
        <div style={{ fontSize: 13, color: "#D1D5DB" }}>Active since {profile.member_since}</div>
        {level && levelStyle && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            marginTop: 10, padding: "6px 14px", borderRadius: 999,
            background: levelStyle.bg, border: `1.5px solid ${levelStyle.border}`,
            color: levelStyle.text, fontSize: 13, fontWeight: 700,
          }}>
            {level === "Bronze" && "🥉"}
            {level === "Silver" && "🥈"}
            {level === "Gold" && "🥇"}
            {level === "Platinum" && "💎"}
            {level} Partner
          </div>
        )}
        {profile.average_rating && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: "#F97316" }}>⭐ {profile.average_rating.avg}</span>
            <span style={{ fontSize: 12, color: "#6B7280" }}>· rated by tenants & landlords ({profile.average_rating.count} reviews)</span>
          </div>
        )}
      </div>
      {Object.keys(c).length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#9CA3AF", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Agent Conduct</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <ConductPill label={c.professionalism} />
            <ConductPill label={c.communication} />
            {c.would_recommend_pct !== undefined && (
              <ConductPill label={`${c.would_recommend_pct}% recommend`} />
            )}
          </div>
        </div>
      )}
      <div style={{ fontSize: 13, color: "#6B7280" }}>
        Deals: <strong style={{ color: "#D1D5DB" }}>{profile.completed_rentals_range} completed</strong>
      </div>
      {profile.city && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#6B7280" }}>
          <MapPin style={{ height: 13, width: 13 }} />
          {profile.city}
        </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function PublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`/api/public/profile/${slug}`)
      .then(r => r.json())
      .then(d => { setProfile(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  const shareUrl = `https://orello.in/u/${slug}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const roleLabel = {
    tenant: "Tenant",
    landlord: "Landlord",
    agent: "Agent",
    broker_admin: "Broker · Agent",
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#080808", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>
      Loading…
    </div>
  );
  if (!profile || profile.detail) return (
    <div style={{ minHeight: "100vh", background: "#080808", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>
      Profile not found.
    </div>
  );

  const isBroker = ["agent", "broker_admin"].includes(profile.user_type);

  return (
    <div style={{ minHeight: "100vh", background: "#080808", color: "#F5F5F5", fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header style={{ padding: "20px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Link to="/"><Logo size="sm" /></Link>
      </header>

      <div style={{ maxWidth: 540, margin: "0 auto", padding: "40px 20px 80px" }}>

        {/* Profile card */}
        <div style={{ background: "#111", borderRadius: 20, border: "1px solid rgba(255,255,255,0.08)", padding: 28, display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Avatar + name row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                height: 64, width: 64, borderRadius: "50%",
                background: "linear-gradient(135deg, #F97316, #8B5CF6)",
                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 26, fontWeight: 700, flexShrink: 0,
              }}>
                {profile.display_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h1 style={{ fontSize: 22, fontWeight: 700, color: "#FFFFFF", margin: 0 }}>{profile.display_name}</h1>
                  {profile.kyc_verified && (
                    <BadgeCheck style={{ height: 20, width: 20, color: "#22C55E", flexShrink: 0 }} />
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  {profile.kyc_verified && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#15803D", background: "#DCFCE7", padding: "2px 8px", borderRadius: 999 }}>✓ Verified</span>
                  )}
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", background: "#1F1F1F", padding: "2px 8px", borderRadius: 999 }}>
                    {roleLabel[profile.user_type] || profile.user_type}
                  </span>
                  <span style={{ fontSize: 11, color: "#6B7280", display: "flex", alignItems: "center", gap: 4 }}>
                    <Calendar style={{ height: 11, width: 11 }} /> Member since {profile.member_since}
                  </span>
                </div>
              </div>
            </div>

            {/* Share button */}
            <button
              onClick={copyLink}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: copied ? "#22C55E" : "rgba(255,255,255,0.07)",
                color: copied ? "#fff" : "#D1D5DB",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8, padding: "7px 12px", fontSize: 12,
                fontWeight: 600, cursor: "pointer", flexShrink: 0,
                transition: "background 0.2s ease",
              }}
            >
              <Copy style={{ height: 13, width: 13 }} />
              {copied ? "Copied!" : "Share"}
            </button>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} />

          {/* Content: new vs has-deals */}
          {profile.is_new ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌟</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#FFFFFF" }}>New to Orello</div>
              <p style={{ fontSize: 13, color: "#6B7280", marginTop: 6 }}>
                No rental history yet — verified and ready to rent.
              </p>
            </div>
          ) : (
            <>
              {profile.user_type === "tenant" && <TenantSection profile={profile} />}
              {profile.user_type === "landlord" && <LandlordSection profile={profile} />}
              {isBroker && <BrokerSection profile={profile} />}
            </>
          )}

          {/* Divider */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} />

          {/* Footer trust line */}
          <p style={{ fontSize: 11, color: "#4B5563", textAlign: "center", lineHeight: 1.5, margin: 0 }}>
            Ratings on Orello are only given by verified parties in a completed rental cycle. They cannot be faked.
          </p>
        </div>
      </div>
    </div>
  );
}
